import React from 'react';
import {BIOMARKER_STATUS_COLORS} from '../../constants/colors';

interface ProgressBarSection {
    width: number; // percentage (0-100)
    color: string;
}

interface ProgressBarProps {
    sections?: ProgressBarSection[];
    indicatorPosition?: number; // percentage (0-100)
    indicatorColor?: string;
    showIndicator?: boolean;
    className?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
    sections = [
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.LOW},
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.NORMAL},
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.HIGH},
    ],
    indicatorPosition = 50,
    indicatorColor,
    showIndicator = true,
    className = '',
}) => {
    // Ensure sections add up to 100%
    const totalWidth = sections.reduce((sum, section) => sum + section.width, 0);
    const normalizedSections = sections.map((section) => ({
        ...section,
        width: (section.width / totalWidth) * 100,
    }));

    // Clamp indicator position to prevent clipping outside the bars
    const clampedPosition = Math.max(2, Math.min(indicatorPosition, 98));

    // Determine indicator color based on position if not provided
    const getIndicatorColor = () => {
        if (indicatorColor) return indicatorColor;

        // Find which section the indicator falls into
        let cumulativeWidth = 0;
        for (const section of normalizedSections) {
            cumulativeWidth += section.width;
            if (indicatorPosition <= cumulativeWidth) {
                return section.color;
            }
        }

        // Fallback to last section color
        return normalizedSections[normalizedSections.length - 1]?.color || BIOMARKER_STATUS_COLORS.NORMAL;
    };

    return (
        <div className={`rounded-2xl p-3 mb-4 relative ${className}`} style={{backgroundColor: '#F0F1EB'}}>
            <div className="relative h-3 flex gap-1">
                {normalizedSections.map((section, index) => (
                    <div
                        key={index}
                        className="h-3 rounded-full"
                        style={{
                            width: `${section.width}%`,
                            backgroundColor: section.color,
                        }}
                    />
                ))}
            </div>

            {showIndicator && (
                <div
                    className="absolute rounded-full z-10"
                    style={{
                        left: `${clampedPosition}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '9px',
                        height: '50px',
                        backgroundColor: getIndicatorColor(),
                        outline: '3px solid #F9F9F7',
                    }}
                />
            )}
        </div>
    );
};

export default ProgressBar;
